/**
 * Landing-page content catalog — namespace "landing" in the CMS.
 *
 * Shared between apps/mobile (renders `cms[key] ?? LANDING_DEFAULTS[key]`)
 * and apps/admin-dashboard (the Content editor lists exactly these keys with
 * friendly labels). Not a Zod schema, but it IS part of the cross-app
 * contract: both sides must agree on the key names, so it lives here.
 */

export const LANDING_NAMESPACE = 'landing';

export const LANDING_DEFAULTS: Record<string, string> = {
  'brand.name': 'Anonymous Help',
  'brand.logoUrl': '',

  'hero.eyebrow': 'Ανώνυμη ψυχολογική υποστήριξη',
  'hero.title': 'Μίλησε σε ψυχολόγο. Χωρίς να πεις ποιος είσαι.',
  'hero.subtitle':
    'Συνεδρίες με αδειούχους ψυχολόγους από το κινητό σου — με πλήρη ανωνυμία, παραποιημένη φωνή και χωρίς κάμερα, αν έτσι νιώθεις πιο άνετα.',
  'hero.cta': 'Ξεκίνα ανώνυμα',
  'hero.secondaryCta': 'Έχω ήδη λογαριασμό',
  'hero.imageUrl': '',

  'trust.1': '🔒 Πλήρης ανωνυμία — δεν χρειάζεται ούτε όνομα',
  'trust.2': '🎓 Μόνο αδειούχοι, ελεγμένοι ψυχολόγοι',
  'trust.3': '📱 Συνεδρίες από όπου κι αν είσαι',

  'how.title': 'Πώς λειτουργεί',
  'how.step1.title': '1. Φτιάξε ανώνυμο λογαριασμό',
  'how.step1.body': 'Μόνο ένα ψευδώνυμο. Χωρίς όνομα, χωρίς τηλέφωνο, χωρίς κάμερα.',
  'how.step2.title': '2. Διάλεξε ψυχολόγο',
  'how.step2.body': 'Δες προφίλ, ειδικότητες και αξιολογήσεις, και βρες αυτόν που σου ταιριάζει.',
  'how.step3.title': '3. Κάνε την πρώτη σου συνεδρία',
  'how.step3.body': 'Κλείσε ραντεβού και μίλησε με ασφάλεια — με αλλοιωμένη φωνή αν το επιλέξεις.',

  'therapists.title': 'Οι ψυχολόγοι μας',
  'therapists.subtitle': 'Επίλεξε κατηγορία για να δεις ποιοι ειδικεύονται σε αυτό που σε απασχολεί.',

  'cta.title': 'Το πρώτο βήμα είναι το πιο δύσκολο. Κάν το ανώνυμα.',
  'cta.body': 'Δεν χρειάζεται να πεις ούτε το όνομά σου για να ζητήσεις βοήθεια.',
  'cta.button': 'Ξεκίνα τώρα — δωρεάν εγγραφή',

  'join.title': 'Είσαι ψυχολόγος;',
  'join.body':
    'Αν θέλεις να ενταχθείς στην ομάδα του Anonymous Help ως θεραπευτής, κάνε δωρεάν την εγγραφή σου στο πιο κάτω link.',
  'join.button': 'Δωρεάν εγγραφή θεραπευτή',
  'join.url': 'https://therapists.anonymoushelp.gr/register',

  'social.facebookUrl': '',
  'social.instagramUrl': '',

  'contact.phone1': '+30 694 825 7557 (Ελλάδα)',
  'contact.phone2': '+357 99 140 329 (Κύπρος)',
  'contact.email': 'info@anonymoushelp.gr',

  'footer.disclaimer':
    'Η πλατφόρμα δεν αντικαθιστά επείγουσα φροντίδα. Αν βρίσκεσαι σε κρίση, κάλεσε 1018 (Γραμμή Παρέμβασης για την Αυτοκτονία), 10306 ή 112.',
};

export interface LandingKeyDef {
  key: string;
  label: string;
  multiline?: boolean;
  image?: boolean;
}

/** Landing keys shown in the admin Content editor, in display order, with labels. */
export const LANDING_KEYS: LandingKeyDef[] = [
  { key: 'brand.name', label: 'Επωνυμία (πάνω αριστερά + footer)' },
  { key: 'brand.logoUrl', label: 'Logo (εμφανίζεται δίπλα/αντί για την επωνυμία)', image: true },
  { key: 'hero.eyebrow', label: 'Hero — μικρός τίτλος (eyebrow)' },
  { key: 'hero.title', label: 'Hero — κύριος τίτλος' },
  { key: 'hero.subtitle', label: 'Hero — υπότιτλος', multiline: true },
  { key: 'hero.cta', label: 'Hero — κουμπί CTA' },
  { key: 'hero.secondaryCta', label: 'Hero — δευτερεύον κουμπί' },
  { key: 'hero.imageUrl', label: 'Hero — εικόνα', image: true },
  { key: 'trust.1', label: 'Trust badge 1' },
  { key: 'trust.2', label: 'Trust badge 2' },
  { key: 'trust.3', label: 'Trust badge 3' },
  { key: 'how.title', label: 'Πώς λειτουργεί — τίτλος' },
  { key: 'how.step1.title', label: 'Βήμα 1 — τίτλος' },
  { key: 'how.step1.body', label: 'Βήμα 1 — κείμενο', multiline: true },
  { key: 'how.step2.title', label: 'Βήμα 2 — τίτλος' },
  { key: 'how.step2.body', label: 'Βήμα 2 — κείμενο', multiline: true },
  { key: 'how.step3.title', label: 'Βήμα 3 — τίτλος' },
  { key: 'how.step3.body', label: 'Βήμα 3 — κείμενο', multiline: true },
  { key: 'therapists.title', label: 'Ενότητα ψυχολόγων — τίτλος' },
  { key: 'therapists.subtitle', label: 'Ενότητα ψυχολόγων — υπότιτλος', multiline: true },
  { key: 'cta.title', label: 'Τελικό CTA — τίτλος' },
  { key: 'cta.body', label: 'Τελικό CTA — κείμενο', multiline: true },
  { key: 'cta.button', label: 'Τελικό CTA — κουμπί' },
  { key: 'join.title', label: 'Ενότητα θεραπευτών — τίτλος' },
  { key: 'join.body', label: 'Ενότητα θεραπευτών — κείμενο', multiline: true },
  { key: 'join.button', label: 'Ενότητα θεραπευτών — κουμπί' },
  { key: 'join.url', label: 'Ενότητα θεραπευτών — link εγγραφής' },
  { key: 'social.facebookUrl', label: 'Facebook — link σελίδας (κενό = δεν εμφανίζεται)' },
  { key: 'social.instagramUrl', label: 'Instagram — link προφίλ (κενό = δεν εμφανίζεται)' },
  { key: 'contact.phone1', label: 'Τηλέφωνο επικοινωνίας 1' },
  { key: 'contact.phone2', label: 'Τηλέφωνο επικοινωνίας 2' },
  { key: 'contact.email', label: 'Email επικοινωνίας' },
  { key: 'footer.disclaimer', label: 'Footer — disclaimer κρίσης', multiline: true },
];

// ── Legal documents (namespace "legal") ────────────────────────────────
// Terms of Use + Privacy Policy, rendered at /terms and /privacy in the
// client app and linked from the landing footer and both register pages.
// Same CMS mechanics as the landing copy: the owner edits/publishes from
// Admin → Content without a redeploy — important because legal texts need
// recurring updates. The defaults below are PLACEHOLDERS: have a lawyer
// produce the real texts before launch.

export const LEGAL_NAMESPACE = 'legal';

export const LEGAL_DEFAULTS: Record<string, string> = {
  'terms.title': 'Όροι Χρήσης',
  'terms.body': [
    'Τελευταία ενημέρωση: [ημερομηνία]',
    '',
    '1. Η πλατφόρμα παρέχει υπηρεσίες διασύνδεσης με αδειούχους ψυχολόγους. Δεν αποτελεί υπηρεσία επείγουσας φροντίδας. Σε περίπτωση κρίσης καλέστε 1018, 10306 ή 112.',
    '',
    '2. Η χρήση της πλατφόρμας επιτρέπεται μόνο σε ενήλικες (18+).',
    '',
    '3. Οι συνεδρίες πραγματοποιούνται από ανεξάρτητους επαγγελματίες, υπεύθυνους για τις υπηρεσίες που παρέχουν.',
    '',
    '4. [Συμπληρώστε τους πλήρεις όρους με τη βοήθεια νομικού συμβούλου — επεξεργασία από Admin → Content.]',
  ].join('\n'),

  'privacy.title': 'Πολιτική Απορρήτου',
  'privacy.body': [
    'Τελευταία ενημέρωση: [ημερομηνία]',
    '',
    '1. Σχεδιάσαμε την πλατφόρμα με προτεραιότητα την ανωνυμία: μπορείτε να εγγραφείτε μόνο με ψευδώνυμο, χωρίς όνομα ή τηλέφωνο.',
    '',
    '2. Τα ευαίσθητα δεδομένα (π.χ. στοιχεία έκτακτης επικοινωνίας) αποθηκεύονται κρυπτογραφημένα (AES-256-GCM).',
    '',
    '3. Δεν πωλούμε ούτε κοινοποιούμε δεδομένα σε τρίτους για διαφημιστικούς σκοπούς.',
    '',
    '4. Έχετε δικαίωμα πρόσβασης, διόρθωσης και διαγραφής των δεδομένων σας (GDPR). Η διαγραφή λογαριασμού γίνεται από τις Ρυθμίσεις.',
    '',
    '5. [Συμπληρώστε την πλήρη πολιτική με τη βοήθεια νομικού συμβούλου / DPO — επεξεργασία από Admin → Content.]',
  ].join('\n'),
};

export const LEGAL_KEYS: LandingKeyDef[] = [
  { key: 'terms.title', label: 'Όροι Χρήσης — τίτλος' },
  { key: 'terms.body', label: 'Όροι Χρήσης — πλήρες κείμενο', multiline: true },
  { key: 'privacy.title', label: 'Πολιτική Απορρήτου — τίτλος' },
  { key: 'privacy.body', label: 'Πολιτική Απορρήτου — πλήρες κείμενο', multiline: true },
];
